import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faUserGraduate,
  faTriangleExclamation,
  faUsers,
  faArrowUpRightFromSquare,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import './MyStudents.css';

const API = process.env.REACT_APP_API_BASE_URL;

const TABS = [
  ['active', 'Active'],
  ['inactive', 'Inactive'],
];

// Same colour rule as before: 1 left = red, 2 left = orange
const leftTone = (left) => (left <= 1 ? 'is-critical' : left === 2 ? 'is-warning' : 'is-ok');

const Photo = ({ src }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="rmams-photo">
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : <FontAwesomeIcon icon={faUserGraduate} />}
    </span>
  );
};

const MyStudents = ({ userData }) => {
  // local tab (the profile page already uses ?tab= for its own tabs)
  const [tab, setTab] = useState('active');

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState('');

  const teacherId = userData?.id;

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/mystudents/${teacherId}`);
        if (!cancelled) setStudents(res.data?.students || []);
      } catch (err) {
        console.error('Error fetching students:', err);
        if (!cancelled) setError('We couldn’t load this teacher’s students.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId, reload]);

  // ---------- Split + search ----------
  const { active, inactive, endingSoon } = useMemo(() => {
    const withLeft = students.map((s) => ({ ...s, left: (s.totalSessions || 0) - (s.sessionsTaken || 0) }));
    const act = withLeft.filter((s) => s.left > 0).sort((a, b) => a.left - b.left); // fewest left first
    const ina = withLeft.filter((s) => s.left <= 0);
    return { active: act, inactive: ina, endingSoon: act.filter((s) => s.left <= 2).length };
  }, [students]);

  const list = tab === 'active' ? active : inactive;
  const q = query.trim().toLowerCase();
  const shown = q ? list.filter((s) => `${s.FirstName} ${s.LastName}`.toLowerCase().includes(q)) : list;

  if (!userData || loading) {
    return (
      <div className="rmams" aria-busy="true">
        <div className="rmams-inner">
          <div className="rmams-skel rmams-skel--bar" />
          <div className="rmams-grid">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rmams-skel rmams-skel--card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rmams">
      <div className="rmams-inner">
        {/* ---------- Header ---------- */}
        <header className="rmams-head">
          <ul className="rmams-stats">
            <li className="is-ok">
              <strong>{active.length}</strong> active
            </li>
            <li className="is-warning">
              <strong>{endingSoon}</strong> ending soon
            </li>
            <li className="is-muted">
              <strong>{inactive.length}</strong> inactive
            </li>
          </ul>
        </header>

        {/* ---------- Tabs + search ---------- */}
        <div className="rmams-bar">
          <div className="rmams-tabs" role="tablist" aria-label="Students">
            {TABS.map(([key, label]) => {
              const n = key === 'active' ? active.length : inactive.length;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={`rmams-tab ${tab === key ? 'is-on' : ''}`}
                  onClick={() => setTab(key)}
                >
                  {label} <span>{n}</span>
                </button>
              );
            })}
          </div>

          <label className="rmams-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span className="rmams-sr">Search students</span>
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name" />
            {query && (
              <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </label>
        </div>

        {/* ---------- Content ---------- */}
        {error ? (
          <div className="rmams-empty">
            <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
            <p>{error}</p>
            <button type="button" className="rmams-btn" onClick={() => setReload((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : shown.length === 0 ? (
          <div className="rmams-empty">
            <FontAwesomeIcon icon={faUsers} />
            <p>
              {q
                ? `No student matches “${query.trim()}”.`
                : tab === 'active'
                ? 'This teacher has no active students right now.'
                : 'No inactive students.'}
            </p>
          </div>
        ) : (
          <ul className="rmams-grid">
            {shown.map((s) => {
              const total = s.totalSessions || 0;
              const taken = Math.min(s.sessionsTaken || 0, total);
              const pct = total ? Math.round((taken / total) * 100) : 0;
              const isActive = s.left > 0;
              return (
                <li key={s.IdUser}>
                  {/* same as before: opens the student's admin profile in a new tab */}
                  <a
                    className={`rmams-card ${isActive ? leftTone(s.left) : 'is-inactive'}`}
                    href={`/Admin/studentprofile/${s.IdUser}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="rmams-card-top">
                      <Photo src={s.Image} />
                      <div className="rmams-who">
                        <h3>
                          {s.FirstName} {s.LastName}
                        </h3>
                        <p>
                          {s.Grade != null && `Grade ${s.Grade}`}
                          {s.Grade != null && s.Age != null && ' · '}
                          {s.Age != null && `Age ${s.Age}`}
                        </p>
                      </div>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="rmams-open" aria-hidden="true" />
                    </div>

                    <div className="rmams-progress">
                      <div className="rmams-progress-row">
                        <span>
                          Session <b>{taken}</b> of {total}
                        </span>
                        <span className="rmams-left">
                          {isActive ? `${s.left} ${s.left === 1 ? 'session' : 'sessions'} left` : 'Package finished'}
                        </span>
                      </div>
                      <span className="rmams-bar-track" aria-hidden="true">
                        <span style={{ width: `${pct}%` }} />
                      </span>
                    </div>
                    <span className="rmams-sr"> (opens in a new tab)</span>
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MyStudents;